// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';

/**
 * Shared presentation for the day-ahead v1 narration cards. Drivers (factors,
 * load impacts) and Actions (requested changes) all use this same shape: a
 * dot-and-label head with a headline figure on the right, labelled prose
 * blocks, and an optional chart beside them.
 */

/**
 * Accent per narration key, so a card's dot, its curve and its headline figure
 * all agree. Keys the optimizer doesn't publish an accent for fall back to the
 * palette by position — kept distinct from the phase-band palette so a line is
 * never the same hue as the shading behind it.
 */
const KEY_ACCENTS: Record<string, string> = {
  temperature: '#ff832b',
  tariff: '#be95ff',
  hvac_load: '#3ddbd9',
  total_grid_load: '#4589ff',
  grid_peak_demand: '#4589ff',
};

const ACCENT_PALETTE = ['#4589ff', '#be95ff', '#3ddbd9', '#ff832b', '#f1c21b'];

export function accentFor(key: string, index: number): string {
  return KEY_ACCENTS[key] ?? ACCENT_PALETTE[index % ACCENT_PALETTE.length];
}

/**
 * Rendered in place of a figure the narration didn't supply.
 *
 * The rule for missing figures, applied throughout the drivers/actions cards:
 * a missing figure renders as `—` when it sits inline among other figures; the
 * element is omitted when the figure *is* the element. So a compare row shows
 * "Recommended —", but a card whose only headline would be "—" has no headline.
 */
export const NO_VALUE = '—';

/**
 * Whether a narration figure is actually a number we can render.
 *
 * The narration is a free-form JSON blob from the Object Store, governed by no
 * OpenAPI contract, so every figure in it is optional in practice however the
 * TypeScript types read. `Number.isFinite` rejects null, undefined, NaN and
 * ±Infinity (all reachable from a `recommended - baseline` subtraction) while
 * still accepting **0**, which is a legitimate value here — chillers off, zero
 * curtailment. A `!value` shortcut would blank those out.
 */
export function isFigure(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Precision scaled to magnitude, because these figures span a tariff rate
 * ($0.28) to a daily energy total (80,086 kWh). A fixed decimal count either
 * rounds the small values into uselessness or adds noise to the large ones.
 */
export function formatMagnitude(value: number | null | undefined): string {
  if (!isFigure(value)) return NO_VALUE;
  const abs = Math.abs(value);
  const decimals = abs >= 100 ? 0 : abs >= 1 ? 1 : 2;
  return value.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

/** Always one decimal, so a column of deltas lines up (-5.0%, +4.4%). */
export function formatPercent(value: number | null | undefined): string {
  if (!isFigure(value)) return NO_VALUE;
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/** A value with its unit, omitting the unit when the quantity is a bare count. */
export function formatWithUnit(value: number | null | undefined, unit: string): string {
  // Returns before the unit is appended: "— °C" reads as a measurement rather
  // than the absence of one.
  if (!isFigure(value)) return NO_VALUE;
  const text = formatMagnitude(value);
  if (!unit || unit.toLowerCase() === 'unitless') return text;
  return `${text} ${unit}`;
}

export function deltaColor(delta: number): string {
  // An unknown delta reads as neutral, never as a saving.
  if (!Number.isFinite(delta)) return 'var(--text-primary)';
  if (delta < 0) return 'var(--st-ok)';
  if (delta > 0) return 'var(--st-warn)';
  return 'var(--text-primary)';
}

export interface NarrationBlock {
  label: string;
  text: string;
}

interface NarrationCardProps {
  accent: string;
  label: string;
  /** Muted qualifier beside the label, e.g. the equipment a change applies to. */
  sublabel?: string;
  /** Omitted when the narration supplied no figure to headline with. */
  headline?: string | null;
  headlineColor?: string;
  blocks: NarrationBlock[];
  chart?: React.ReactNode;
}

export function NarrationCard({
  accent,
  label,
  sublabel,
  headline,
  headlineColor,
  blocks,
  chart,
}: NarrationCardProps) {
  return (
    <div className="pd-card">
      <div className="pd-card-head">
        <span className="pd-card-label">
          <span className="pd-card-dot" style={{ background: accent }} aria-hidden />
          {label}
          {sublabel && <span className="pd-card-sublabel">{sublabel}</span>}
        </span>
        {headline && (
          <span className="pd-card-headline" style={{ color: headlineColor ?? accent }}>
            {headline}
          </span>
        )}
      </div>

      <div className="pd-card-body">
        <div className="pd-card-prose">
          {blocks.map(block => (
            <div className="pd-card-block" key={block.label}>
              <span className="pd-card-block-label">{block.label}</span>
              <p className="pd-card-text">{block.text}</p>
            </div>
          ))}
        </div>

        {chart && <div className="pd-card-chart">{chart}</div>}
      </div>
    </div>
  );
}

export function SeriesLegend({ accent }: { accent: string }) {
  return (
    <>
      <span className="pd-legend-item">
        <span className="pd-legend-dash pd-legend-dash-baseline" aria-hidden />
        Baseline
      </span>
      <span className="pd-legend-item">
        <span className="pd-legend-dash" style={{ borderTop: `2px solid ${accent}` }} aria-hidden />
        Recommended
      </span>
    </>
  );
}

/** Footnote row for conditions the plan explicitly did not act on. */
export function NarrationNote({ accent, text }: { accent: string; text: string }) {
  return (
    <div className="pd-note">
      <span className="pd-card-dot" style={{ background: accent }} aria-hidden />
      {text}
    </div>
  );
}
